import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { ApiError } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.js'
import { Button, Input } from '../../ui/index.js'
import { usePageTitle } from '../../lib/page-title.js'
import { AuthLayout } from './AuthLayout.js'
import { FieldError, FormError } from './FormError.js'


export function AcceptInvite() {
  usePageTitle('Accept invite')
  const { api, signIn } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setFormError(null)

    const next: Record<string, string> = {}
    if (password.length === 0) next['password'] = 'Enter a password'
    setErrors(next)
    if (Object.keys(next).length > 0 || token === null) return

    setBusy(true)
    try {
      const body: { token: string; password: string; name?: string } = {
        token,
        password,
      }
      if (name.trim() !== '') body.name = name.trim()
      const result = await api.post<{ accessToken: string; refreshToken: string }>('/api/invites/accept', body)
      await signIn(result)
      navigate('/', { replace: true })
    } catch (error) {
      if (error instanceof ApiError && error.isValidation && error.details !== undefined) {
        setErrors(error.details)
      } else if (error instanceof ApiError) {
        setFormError(error.message)
      } else {
        setFormError('Something went wrong. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  if (token === null) {
    return (
      <AuthLayout title="Accept invitation" subtitle="Finish setting up your account.">
        <p>This link is incomplete — ask your manager to send the invitation again.</p>
        <div style={{ marginTop: 'var(--o-space-5)' }}>
          <Link to="/login">Sign in</Link>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Accept invitation" subtitle="Finish setting up your account.">
      <form onSubmit={submit} noValidate>
        <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
          <label className="field-label" htmlFor="name">
            Name
          </label>
          <Input
            id="name"
            name="name"
            type="text"
            value={name}
            onChange={setName}
          />
          <FieldError message={errors['name']} />
        </div>
        <div className="field" style={{ marginBottom: 'var(--o-space-5)' }}>
          <label className="field-label" htmlFor="password">
            Password
          </label>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={setPassword}
          />
          <FieldError message={errors['password']} />
        </div>
        <FormError message={formError} />
        <Button type="submit" kind="primary" disabled={busy}>
          Activate
        </Button>
      </form>
    </AuthLayout>
  )
}
